import { useEffect, useMemo, useState } from 'react';
import { builder, BuilderContent } from '@builder.io/sdk';
import { ArticlePreview, BuilderArticlePageReference } from '@/types/article';

const ARTICLE_FIELDS =
  'data.title,data.url,data.heroImage,data.category,data.tags,data.featured,lastUpdated,firstPublished';

function initBuilderSdk(): boolean {
  if (!process.env.NEXT_PUBLIC_BUILDER_API_KEY) return false;
  builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY);
  return true;
}

function mapBuilderResults(results: BuilderContent[]): ArticlePreview[] {
  return results
    .filter((article) => article.data)
    .map((article) => ({
      ...article.data,
      lastUpdated: article.lastUpdated,
      firstPublished: article.firstPublished,
    })) as ArticlePreview[];
}

interface UseBuilderArticlesOptions {
  autoSelectLatest: boolean;
  limit: number;
  latestArticles: ArticlePreview[] | undefined;
  articleInputs: { article?: BuilderArticlePageReference }[];
}

/**
 * Resolves articles for Builder components that support both auto-select and
 * manual modes. Handles two Builder SDK editor limitations:
 *
 * 1. Server-fetched state data (featuredArticles) isn't preserved in the
 *    editor's client-side state, so auto-select mode needs a client-side
 *    fallback fetch.
 * 2. Nested references (e.g. category within article) aren't resolved in
 *    the editor's content state, so manual articles need client-side
 *    enrichment to display category tags.
 */
export function useBuilderArticles({
  autoSelectLatest,
  limit,
  latestArticles,
  articleInputs,
}: UseBuilderArticlesOptions): ArticlePreview[] {
  // --- Auto-select fallback ---
  const [fallbackArticles, setFallbackArticles] = useState<ArticlePreview[]>([]);

  useEffect(() => {
    if (!autoSelectLatest || (latestArticles && latestArticles.length > 0) || fallbackArticles.length > 0) return;
    if (!initBuilderSdk()) return;

    builder
      .getAll('article', {
        fields: ARTICLE_FIELDS,
        limit,
        sort: { firstPublished: -1 },
        options: { noTargeting: true, includeRefs: true },
      })
      .then((results) => setFallbackArticles(mapBuilderResults(results)));
  }, [autoSelectLatest, latestArticles, limit, fallbackArticles.length]);

  // --- Manual article mapping ---
  const mappedArticles = useMemo(
    () => articleInputs.map((a) => a.article?.value?.data).filter((data) => !!data),
    [articleInputs]
  );

  // --- Manual article category enrichment ---
  const [enrichedManualArticles, setEnrichedManualArticles] = useState<ArticlePreview[]>([]);
  const manualArticleUrls = useMemo(() => mappedArticles.map((a) => a.url).filter(Boolean), [mappedArticles]);

  useEffect(() => {
    if (autoSelectLatest || manualArticleUrls.length === 0) return;

    const hasUnresolvedCategories = mappedArticles.some((a) => a.category?.id && !a.category?.value);
    if (!hasUnresolvedCategories) return;
    if (!initBuilderSdk()) return;

    builder
      .getAll('article', {
        fields: ARTICLE_FIELDS,
        query: { 'data.url': { $in: manualArticleUrls } },
        options: { noTargeting: true, includeRefs: true },
      })
      .then((results) => {
        const enriched = mapBuilderResults(results);
        const enrichedMap = new Map(enriched.map((a) => [a.url, a]));
        setEnrichedManualArticles(
          manualArticleUrls.map((url) => enrichedMap.get(url)).filter(Boolean) as ArticlePreview[]
        );
      });
  }, [autoSelectLatest, manualArticleUrls, mappedArticles]);

  // --- Resolve final article list ---
  if (autoSelectLatest) {
    const resolved = latestArticles?.length ? latestArticles : fallbackArticles;
    return resolved.slice(0, limit);
  }

  return enrichedManualArticles.length ? enrichedManualArticles : mappedArticles;
}
